import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart2,
  BookOpen,
  Code2,
  DollarSign,
  ExternalLink,
  Home,
  KeyRound,
  LogOut,
  Menu,
  ScrollText,
  Settings as SettingsIcon,
  ShoppingCart,
  Smartphone,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import getConfig from '../config/environments';

const config = getConfig();

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/', icon: Home },
    ],
  },
  {
    label: 'Customer Operations',
    items: [
      { name: 'Customers', href: '/customers', icon: Users },
    ],
  },
  {
    label: 'Trading',
    items: [
      { name: 'Orders', href: '/orders', icon: ShoppingCart },
      { name: 'Portfolio', href: '/portfolio', icon: BarChart2 },
      { name: 'Cash In/Out', href: '/financials', icon: DollarSign },
    ],
  },
  {
    label: 'Developers',
    items: [
      { name: 'Overview', href: '/developers', icon: Code2 },
      {
        name: 'API Reference',
        href: `${config.api.gatewayURL}/redoc`,
        icon: BookOpen,
        external: true,
      },
      { name: 'Embedded App', href: '/developers/embed', icon: Smartphone },
      { name: 'API Clients', href: '/api-clients', icon: KeyRound },
    ],
  },
  {
    label: 'Administration',
    items: [
      { name: 'Team', href: '/team', icon: Users },
      { name: 'Audit log', href: '/audit', icon: ScrollText },
      { name: 'Settings', href: '/settings', icon: SettingsIcon },
    ],
  },
];

const roleLabel = (r) => (r ? r.charAt(0) + r.slice(1).toLowerCase() : 'Member');

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      navigate('/login', { replace: true });
    }
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const initials = (user?.name || user?.email || 'U')
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('');

  return (
    <div className="min-h-screen bg-ink-50 flex">
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-ink-900/30 lg:hidden"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-ink-900 flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-16 px-5 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo-white.svg"
              alt="Tabadulat"
              className="h-7 w-auto"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <p className="text-white/40 text-2xs leading-tight">Partner Portal</p>
          </div>
          <button
            type="button"
            className="lg:hidden text-white/60 hover:text-white"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="nav-group-label text-white/30">{group.label}</p>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = !item.external && isActive(item.href);
                const linkClass = `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`;

                if (item.external) {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1">{item.name}</span>
                      <ExternalLink className="h-3 w-3 opacity-40 shrink-0" />
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={linkClass}
                  >
                    {active && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-accent-400 rounded-full" />
                    )}
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.name}</span>
                    {item.comingSoon && (
                      <span className="shrink-0 text-2xs font-medium leading-none px-1.5 py-0.5 rounded-full bg-white/10 text-white/50 border border-white/10">
                        Soon
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-white text-xs font-semibold shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-white/40 text-2xs truncate">{user?.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-1 w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-ink-200 h-16 flex items-center px-4 sm:px-6 gap-3">
          <button
            type="button"
            className="lg:hidden p-2 -ml-2 rounded-md text-ink-600 hover:bg-ink-100"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden sm:flex items-center">
            <Activity className="h-3.5 w-3.5 text-success-500" />
          </div>

          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-2">
            {user?.profile?.org_role && (
              <span className="text-xs font-medium px-2 py-1 rounded-md bg-accent-50 text-accent-700 border border-accent-100">
                {roleLabel(user.profile.org_role)}
              </span>
            )}
            <span className="text-xs text-ink-500 tabular-nums">
              {user?.email}
            </span>
          </div>
        </header>

        <main className={`flex-1 w-full ${
          location.pathname.startsWith('/orders')
            ? 'p-4 sm:p-6 w-full'
            : 'p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto'
        }`}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
